import {
  getLocalVideoDuration,
  getVideoDurationLimitError,
  getUploadedResourceKind,
  uploadResourceLimits,
  validateLocalUploadFile,
  type UploadedResource,
  type UploadResourceLimitKind,
} from "./uploadedResources"
import { isAbortError } from "~/common/utils"
import { uploadFile } from "~/api/upload"

export type LocalUploadResourceKind = UploadResourceLimitKind

export type LocalUploadFile = {
  file: File
  kind: LocalUploadResourceKind
}

export type ResourceTransferPayload =
  | { files: File[]; kind: "files" }
  | { kind: "remote-image"; url: string }
  | { kind: "empty" }

export type UploadLocalResourceFailure = {
  error: unknown
  file: LocalUploadFile
}

export type UploadLocalResourcesResult = {
  failures: UploadLocalResourceFailure[]
  resources: UploadedResource[]
}

export const getUploadFailureMessages = (
  failures: UploadLocalResourceFailure[],
  fallbackMessage = "上传失败，请稍后重试",
) => {
  return Array.from(
    new Set(
      failures.map(({ error }) => {
        return error instanceof Error ? error.message : fallbackMessage
      }),
    ),
  )
}

export const getLocalUploadFileKey = (file: File) => {
  return [file.name, file.size, file.lastModified, file.type].join(":")
}

type GetUploadableLocalFilesOptions = {
  allowedKinds: readonly LocalUploadResourceKind[]
  files: File[]
}

type UploadLocalResourcesOptions = {
  files: LocalUploadFile[]
  onVideoCompressionProgress?: (file: File, progress: number | null) => void
  signal?: AbortSignal
  videoTargetSizeMegabytes?: number
}

const isLocalUploadResourceKind = (
  kind: string,
): kind is LocalUploadResourceKind => {
  return kind === "image" || kind === "video" || kind === "audio"
}

const getLocalUploadFileKind = (file: Pick<File, "name" | "type">) => {
  const resourceKind = getUploadedResourceKind({
    name: file.name,
    type: file.type,
    uploadedAt: Date.now(),
    url: "",
  })

  return isLocalUploadResourceKind(resourceKind) ? resourceKind : undefined
}

export const getResourceKindLabel = (kind: LocalUploadResourceKind) => {
  switch (kind) {
    case "image":
      return "图片"
    case "video":
      return "视频"
    default:
      return "音频"
  }
}

export const getRemoteResourceFetchErrorMessage = (
  kind: LocalUploadResourceKind,
) => {
  return `无法获取远程${getResourceKindLabel(kind)}，可能是网页禁止跨域访问，请下载后本地上传`
}

const getExtensionFromMimeType = (
  mimeType: string,
  kind: LocalUploadResourceKind,
) => {
  switch (mimeType) {
    case "image/png":
      return "png"
    case "image/jpg":
    case "image/jpeg":
      return "jpg"
    case "video/mp4":
      return "mp4"
    case "audio/mpeg":
      return "mp3"
    case "audio/wav":
      return "wav"
    default:
      return kind === "image" ? "jpg" : kind === "video" ? "mp4" : "mp3"
  }
}

const getRemoteFilename = ({
  fallbackName,
  kind,
  mimeType,
  url,
}: {
  fallbackName: string
  kind: LocalUploadResourceKind
  mimeType: string
  url: string
}) => {
  try {
    const pathname = new URL(url).pathname
    const filename = decodeURIComponent(pathname.split("/").pop() || "")

    if (filename.includes(".")) return filename
  } catch {
    // URL 解析失败时使用兜底文件名。
  }

  return `${fallbackName}.${getExtensionFromMimeType(mimeType, kind)}`
}

export const normalizeRemoteUrl = (value: string) => {
  const trimmedValue = value.trim()

  if (!trimmedValue) return ""

  try {
    const url = new URL(trimmedValue, window.location.href)

    if (url.protocol === "http:" || url.protocol === "https:") {
      return url.toString()
    }

    if (url.protocol === "data:" && url.toString().startsWith("data:image/")) {
      return url.toString()
    }
  } catch {
    return ""
  }

  return ""
}

const extractUrlFromText = (value: string) => {
  const matched = value.match(/(?:https?:\/\/|data:image\/)[^\s"'<>]+/i)

  return matched ? normalizeRemoteUrl(matched[0]) : ""
}

const getStandaloneRemoteUrl = (value: string) => {
  const trimmedValue = value.trim()

  if (!trimmedValue || /\s/.test(trimmedValue)) return ""

  try {
    const url = new URL(trimmedValue)

    if (url.protocol === "http:" || url.protocol === "https:") {
      return url.toString()
    }

    if (url.protocol === "data:" && trimmedValue.startsWith("data:image/")) {
      return url.toString()
    }
  } catch {
    return ""
  }

  return ""
}

const extractImageUrlFromHtml = (html: string) => {
  if (!html.trim()) return ""

  const documentValue = new DOMParser().parseFromString(html, "text/html")
  const imageUrl =
    documentValue.querySelector("img")?.getAttribute("src") ||
    documentValue
      .querySelector('meta[property="og:image"]')
      ?.getAttribute("content") ||
    ""

  return normalizeRemoteUrl(imageUrl)
}

const getTransferDataValue = (
  data: DataTransfer | null,
  type: "text/html" | "text/plain" | "text/uri-list",
) => {
  if (!data || !Array.from(data.types).includes(type)) return ""

  return data.getData(type)
}

const getStrictImageUrlFromTransferData = (data: DataTransfer | null) => {
  const htmlUrl = extractImageUrlFromHtml(
    getTransferDataValue(data, "text/html"),
  )

  if (htmlUrl) return htmlUrl

  const uriListUrl = extractUrlFromText(
    getTransferDataValue(data, "text/uri-list"),
  )

  if (uriListUrl) return uriListUrl

  return getStandaloneRemoteUrl(getTransferDataValue(data, "text/plain"))
}

export const getFilesFromDataTransfer = (data: DataTransfer | null) => {
  if (!data) return []

  // files 与 items 中的文件项是同一批数据，不能合并：剪贴板图片（如飞书复制）
  // 每次读取都会新建 File 且 lastModified 取当前时间，合并去重会漏判而重复上传。
  const files = Array.from(data.files)
  if (files.length) return files

  return Array.from(data.items).flatMap((item) => {
    const file = item.kind === "file" ? item.getAsFile() : null
    return file ? [file] : []
  })
}

export const getResourceTransferPayload = (
  data: DataTransfer | null,
  options: { allowRemoteImage: boolean },
): ResourceTransferPayload => {
  const files = getFilesFromDataTransfer(data)

  if (files.length) {
    return { files, kind: "files" }
  }

  if (!options.allowRemoteImage) return { kind: "empty" }

  const url = getStrictImageUrlFromTransferData(data)

  return url ? { kind: "remote-image", url } : { kind: "empty" }
}

export const hasResourceTransferData = (
  data: DataTransfer | null,
  options: { allowRemoteImage: boolean },
) => {
  if (!data) return false

  const transferTypes = Array.from(data.types)

  if (transferTypes.includes("Files")) return true
  if (!options.allowRemoteImage) return false

  return transferTypes.some((type) => {
    return (
      type === "text/html" || type === "text/plain" || type === "text/uri-list"
    )
  })
}

export const fetchRemoteResourceFile = async ({
  fallbackName,
  kind,
  signal,
  url,
}: {
  fallbackName: string
  kind: LocalUploadResourceKind
  signal?: AbortSignal
  url: string
}) => {
  let response: Response

  try {
    response = await fetch(url, {
      credentials: "omit",
      referrerPolicy: "no-referrer",
      signal,
    })
  } catch (error) {
    if (isAbortError(error)) {
      throw error
    }

    throw new Error(getRemoteResourceFetchErrorMessage(kind), { cause: error })
  }

  if (!response.ok) {
    throw new Error(getRemoteResourceFetchErrorMessage(kind))
  }

  const blob = await response.blob()
  const responseMimeType =
    blob.type || response.headers.get("content-type")?.split(";")[0] || ""
  const uploadLimit = uploadResourceLimits[kind]

  if (
    responseMimeType &&
    !uploadLimit.mimeTypes.includes(responseMimeType as never) &&
    responseMimeType !== "application/octet-stream"
  ) {
    throw new Error(
      `远程${getResourceKindLabel(kind)}格式仅支持 ${uploadLimit.extensions.join(" / ")}`,
    )
  }
  const normalizedMimeType =
    responseMimeType && responseMimeType !== "application/octet-stream"
      ? responseMimeType
      : uploadLimit.mimeTypes[0]
  const filename = getRemoteFilename({
    fallbackName,
    kind,
    mimeType: normalizedMimeType,
    url,
  })

  return new File([blob], filename, { type: normalizedMimeType })
}

export const getUploadableLocalFiles = ({
  allowedKinds,
  files,
}: GetUploadableLocalFilesOptions) => {
  const allowedKindSet = new Set<LocalUploadResourceKind>(allowedKinds)
  const acceptedFiles: LocalUploadFile[] = []
  const rejectedFiles: File[] = []
  const validationErrors: string[] = []

  files.forEach((file) => {
    const kind = getLocalUploadFileKind(file)

    if (!kind || !allowedKindSet.has(kind)) {
      rejectedFiles.push(file)
      return
    }

    const validation = validateLocalUploadFile(file, kind)

    if (!validation.valid) {
      validationErrors.push(validation.message)
      return
    }

    acceptedFiles.push({
      file,
      kind,
    })
  })

  return {
    acceptedFiles,
    rejectedFiles,
    validationErrors: Array.from(new Set(validationErrors)),
  }
}

export const validateLocalUploadVideoDurations = async (
  files: LocalUploadFile[],
  options: { enforce?: boolean } = {},
) => {
  if (options.enforce === false) {
    return { acceptedFiles: files, validationErrors: [] }
  }

  const validationResults = await Promise.all(
    files.map(async (localFile) => {
      if (localFile.kind !== "video") {
        return { localFile, validationError: "" }
      }

      try {
        const duration = await getLocalVideoDuration(localFile.file)

        return {
          localFile,
          validationError: getVideoDurationLimitError(duration),
        }
      } catch {
        return {
          localFile,
          validationError: "读取视频时长失败，请重新选择视频",
        }
      }
    }),
  )

  return {
    acceptedFiles: validationResults
      .filter(({ validationError }) => !validationError)
      .map(({ localFile }) => localFile),
    validationErrors: Array.from(
      new Set(
        validationResults
          .map(({ validationError }) => validationError)
          .filter(Boolean),
      ),
    ),
  }
}

const createLocalUploadResource = (file: File, index = 0): UploadedResource => {
  return {
    name: file.name,
    size: file.size,
    type: file.type,
    uploadedAt: Date.now() + index,
    url: `local-upload://${index}/${encodeURIComponent(file.name)}`,
  }
}

export const createLocalUploadResources = (files: File[]) => {
  return files.map((file, index) => createLocalUploadResource(file, index))
}

// 并行上传，单个文件失败不影响其它文件，按原顺序收集成功与失败结果
export const settleUploads = async <TResource>(
  files: LocalUploadFile[],
  upload: (file: LocalUploadFile) => Promise<TResource>,
) => {
  const settledUploads = await Promise.allSettled(files.map(upload))
  const failures: UploadLocalResourceFailure[] = []
  const resources: TResource[] = []

  settledUploads.forEach((settledUpload, index) => {
    const file = files[index]

    if (!file) return

    if (settledUpload.status === "fulfilled") {
      resources.push(settledUpload.value)
      return
    }

    failures.push({ error: settledUpload.reason, file })
  })

  return { failures, resources }
}

export const uploadLocalResources = ({
  files,
  onVideoCompressionProgress,
  signal,
  videoTargetSizeMegabytes,
}: UploadLocalResourcesOptions): Promise<UploadLocalResourcesResult> => {
  return settleUploads(files, async ({ file, kind }) => {
    const result = await uploadFile<UploadedResource>({
      file,
      onVideoCompressionProgress: onVideoCompressionProgress
        ? (progress) => onVideoCompressionProgress(file, progress)
        : undefined,
      signal,
      type: kind,
      videoTargetSizeMegabytes,
    })

    if (!result.data) throw new Error("上传失败，请稍后重试")
    return result.data
  })
}
